import { DragDropModule } from '@angular/cdk/drag-drop';
import { AbcpChartOfAccountsComponent } from './components/chart-of-accounts/chart-of-accounts.component';
import { AbcpClosingOverviewComponent } from './components/closing-overview/closing-overview.component';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AumicoComponentsModule } from '@modeso/aumico-components';
import { AumicoCoreModule } from '@modeso/aumico-lib-core-fe';
import { I18NextModule } from 'angular-i18next';
import { LucideAngularModule } from 'lucide-angular';
import { AbcpAccountAdjustmentsComponent } from './components/account-adjustments/account-adjustments.component';
import { AbcpAccountDocumentsComponent } from './components/account-documents/account-documents.component';
import { AbcpAccountLedgerComponent } from './components/account-ledger/account-ledger.component';
import { AbcpAccountNotesComponent } from './components/account-notes/account-notes.component';
import { AbcpAccountTodosComponent } from './components/account-todos/account-todos.component';
import { AbcpMentionInputComponent } from './components/mention-input/mention-input.component';
import { AbcpNoteDetailComponent } from './components/note-detail/note-detail.component';
import { AbcpNotesOverviewComponent } from './components/notes-overview/notes-overview.component';
import { AbcpAccountWorkspaceComponent } from './components/account-workspace/account-workspace.component';
import { AbcpAccountsOverviewComponent } from './components/accounts-overview/accounts-overview.component';
import { AbcpActivityLogComponent } from './components/activity-log/activity-log.component';
import { ClosingConstellationComponent } from './components/closing-constellation/closing-constellation.component';
import { ClosingImportBannerComponent } from './components/closing-import-banner/closing-import-banner.component';
import { ClosingSetupWizardComponent } from './components/closing-setup-wizard/closing-setup-wizard.component';
import { AbcpAdjustmentDetailComponent } from './components/adjustment-detail/adjustment-detail.component';
import { AbcpAdjustmentModalComponent } from './components/adjustment-modal/adjustment-modal.component';
import { AbcpAdjustmentStatusComponent } from './components/adjustment-status/adjustment-status.component';
import { AbcpClosingAdjustmentsComponent } from './components/closing-adjustments/closing-adjustments.component';
import { AbcpDocumentDetailComponent } from './components/document-detail/document-detail.component';
import { AbcpDocumentPreviewComponent } from './components/document-preview/document-preview.component';
import { AbcpPdfViewerComponent } from './components/pdf-viewer/pdf-viewer.component';
import {
    AbcpDocumentNotesSlotDirective,
    AbcpDocumentViewerSlotDirective,
} from './components/document-detail/document-detail-slots.directive';
import { AbcpDocumentsHubComponent } from './components/documents-hub/documents-hub.component';
import { AbcpSortHeaderComponent } from './components/sort-header/sort-header.component';
import { AbcpStatusPieComponent } from './components/status-pie/status-pie.component';
import { ErpFileImportComponent } from './components/erp-file-import/erp-file-import.component';
import { ErpSyncProgressComponent } from './components/erp-sync-progress/erp-sync-progress.component';
import { ErpSyncComponent } from './components/erp-sync/erp-sync.component';
import { AbcpUploadModalComponent } from './components/upload-modal/upload-modal.component';
import { AbcpWorkspaceHeaderComponent } from './components/workspace-header/workspace-header.component';

//REV-21359 — Präsentations-Modul der ABCP-Pure-Views.
//REV-21367 — `LucideAngularModule` kommt für den Dokumentenhub hinzu: AumicoComponentsModule
//liefert nur den LUCIDE_ICONS-Provider, exportiert die `lucide-icon`-Komponente aber nicht.
//REV-21368 — die Einzeldokument-Seite kommt dazu; sie teilt sich das Vokabular mit dem Hub
//(`lib/types/closing-documents.types.ts`) und braucht keine weiteren Modul-Importe.
//REV-21363 — drei Komponenten kommen dazu: der Konto-Dokumentbereich, das geteilte Upload-Modal
//und die aus `document-detail` EXTRAHIERTE Vorschau. Ebenfalls ohne neue Modul-Importe:
//`aum-select` (Konto-Bereich) kommt über AumicoCoreModule → AumicoPatternModule, das bereits
//importiert ist.
//REV-21362 — das Kontenblatt kommt dazu, ebenfalls ohne neue Modul-Importe: das Template braucht
//nur CommonModule (*ngIf/*ngFor/*ngTemplateOutlet) und die i18next-Pipe.
// REV-21360 / REV-21361 / REV-21366 add pure-view components and required modules.
// REV-21354 — the Setup-Wizard and its launch screen. No new module imports: both need only
// CommonModule, the i18next pipe, `ham-modal` (AumicoComponentsModule) and `lucide-icon`.
// REV-21365 — der Konto-Bereich „Kommentare" kommt dazu (ADR-0019). Ohne neue Modul-Importe:
// das Template braucht CommonModule, die i18next-Pipe und `lucide-icon` — alle drei sind da.
@NgModule({
    declarations: [
        AbcpAccountsOverviewComponent,
        // REV-22479 — the Kontenplan.
        AbcpChartOfAccountsComponent,
        // REV-22476 — the Jahresabschluss overview.
        AbcpClosingOverviewComponent,
        ErpSyncComponent,
        ErpSyncProgressComponent,
        // REV-22007 — the workspace's "ERP" button for a closing fed from a file.
        ErpFileImportComponent,
        AbcpAccountWorkspaceComponent,
        AbcpAccountDocumentsComponent,
        AbcpAccountLedgerComponent,
        AbcpActivityLogComponent,
        AbcpAccountNotesComponent,
        AbcpAccountTodosComponent,
        AbcpMentionInputComponent,
        AbcpNotesOverviewComponent,
        AbcpNoteDetailComponent,
        // REV-21370 / REV-21364 — the correction-booking surfaces (ADR-0015). FIVE components and
        // that is §9: the closing-wide register and the in-account list are NOT one component with a
        // mode (different columns, controls and widths); what they share is the row type and the
        // status chip. The two dialogs ARE one component — see its own doc for why that is not the
        // same case.
        AbcpAdjustmentStatusComponent,
        AbcpClosingAdjustmentsComponent,
        AbcpAccountAdjustmentsComponent,
        AbcpAdjustmentModalComponent,
        AbcpAdjustmentDetailComponent,
        AbcpDocumentsHubComponent,
        AbcpSortHeaderComponent,
        AbcpStatusPieComponent,
        AbcpDocumentDetailComponent,
        AbcpDocumentPreviewComponent,
        AbcpPdfViewerComponent,
        AbcpDocumentViewerSlotDirective,
        AbcpDocumentNotesSlotDirective,
        AbcpUploadModalComponent,
        AbcpWorkspaceHeaderComponent,
        ClosingSetupWizardComponent,
        ClosingImportBannerComponent,
        ClosingConstellationComponent,
    ],
    imports: [
        CommonModule,
        AumicoComponentsModule,
        AumicoCoreModule,
        DragDropModule,
        FormsModule,
        ReactiveFormsModule,
        I18NextModule,
        LucideAngularModule,
    ],
    exports: [
        AbcpAccountsOverviewComponent,
        AbcpChartOfAccountsComponent,
        AbcpClosingOverviewComponent,
        ErpSyncComponent,
        ErpSyncProgressComponent,
        ErpFileImportComponent,
        AbcpAccountWorkspaceComponent,
        AbcpAccountDocumentsComponent,
        AbcpAccountLedgerComponent,
        AbcpActivityLogComponent,
        AbcpAccountNotesComponent,
        AbcpAccountTodosComponent,
        AbcpMentionInputComponent,
        AbcpNotesOverviewComponent,
        AbcpNoteDetailComponent,
        // REV-21370 / REV-21364 — the correction-booking surfaces (ADR-0015). FIVE components and
        // that is §9: the closing-wide register and the in-account list are NOT one component with a
        // mode (different columns, controls and widths); what they share is the row type and the
        // status chip. The two dialogs ARE one component — see its own doc for why that is not the
        // same case.
        AbcpAdjustmentStatusComponent,
        AbcpClosingAdjustmentsComponent,
        AbcpAccountAdjustmentsComponent,
        AbcpAdjustmentModalComponent,
        AbcpAdjustmentDetailComponent,
        AbcpDocumentsHubComponent,
        AbcpSortHeaderComponent,
        AbcpStatusPieComponent,
        AbcpDocumentDetailComponent,
        AbcpDocumentPreviewComponent,
        AbcpPdfViewerComponent,
        AbcpDocumentViewerSlotDirective,
        AbcpDocumentNotesSlotDirective,
        AbcpUploadModalComponent,
        AbcpWorkspaceHeaderComponent,
        ClosingSetupWizardComponent,
        ClosingImportBannerComponent,
        ClosingConstellationComponent,
    ],
})
export class AbcpUiModule {}
